import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import './KnowledgeTutorialModalContent.css';
import { tutorialCollections } from '../../../pages/gallery/Gallery/tutorialContent';

const fallbackSlides = tutorialCollections[0]?.slides || [];

const clampIndex = (value, max) => {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > max) return max;
  return value;
};

const KnowledgeTutorialModalContent = ({ slides = fallbackSlides, initialIndex = 0, onClose }) => {
  const resolvedSlides = useMemo(() => {
    if (Array.isArray(slides) && slides.length > 0) {
      return slides;
    }
    return fallbackSlides;
  }, [slides]);

  const [currentIndex, setCurrentIndex] = useState(() =>
    clampIndex(initialIndex, resolvedSlides.length - 1),
  );

  const totalSlides = resolvedSlides.length;
  const currentSlide = resolvedSlides[currentIndex];

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNext = () => {
    if (currentIndex >= totalSlides - 1) {
      onClose?.();
      return;
    }
    setCurrentIndex((prev) => Math.min(prev + 1, totalSlides - 1));
  };

  return (
    <div className="tutorial-modal">
      <button
        type="button"
        className="tutorial-modal__close"
        onClick={onClose}
        aria-label="Cerrar tutorial"
      >
        ×
      </button>
      <div
        className="tutorial-modal__media"
        style={{ background: currentSlide?.accent || 'linear-gradient(135deg,#d6e6ff,#f3f8ff)' }}
      >
        {currentSlide?.image ? (
          <img src={currentSlide.image} alt={currentSlide.title} />
        ) : (
          <div className="tutorial-modal__media-placeholder">
            <span>Vista previa</span>
          </div>
        )}
      </div>
      <div className="tutorial-modal__content">
        <p className="tutorial-modal__counter">
          Paso {currentIndex + 1} de {totalSlides}
        </p>
        <h3>{currentSlide?.title}</h3>
        {currentSlide?.paragraphs?.map((text, idx) => (
          <p key={`paragraph-${currentSlide.id}-${idx}`}>{text}</p>
        ))}
      </div>
      <div className="tutorial-modal__footer">
        <div className="tutorial-modal__dots" aria-label="Progreso del tutorial">
          {resolvedSlides.map((slide, dotIndex) => (
            <span
              key={`dot-${slide.id}`}
              className={`tutorial-modal__dot${dotIndex === currentIndex ? ' is-active' : ''}`}
            />
          ))}
        </div>
        <div className="tutorial-modal__actions">
          <button
            type="button"
            className="tutorial-modal__nav"
            onClick={handlePrev}
            disabled={currentIndex === 0}
          >
            ‹
          </button>
          <button
            type="button"
            className="tutorial-modal__nav tutorial-modal__nav--primary"
            onClick={handleNext}
          >
            {currentIndex === totalSlides - 1 ? 'Finalizar' : 'Siguiente'}
          </button>
        </div>
      </div>
    </div>
  );
};

KnowledgeTutorialModalContent.propTypes = {
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      paragraphs: PropTypes.arrayOf(PropTypes.string),
      accent: PropTypes.string,
      image: PropTypes.string,
    }),
  ),
  initialIndex: PropTypes.number,
  onClose: PropTypes.func,
};

KnowledgeTutorialModalContent.defaultProps = {
  slides: fallbackSlides,
  initialIndex: 0,
  onClose: undefined,
};

export default KnowledgeTutorialModalContent;
